import { getEtSessionDateKey } from './sessionBoundary.js'
import { toChartTimestamp } from '../timezone.js'
import { getMarketSession } from '../marketSession.js'

export const DYNAMIC_CANVAS_TIMEFRAMES = ['1min', '2min']
export const DEFAULT_DYNAMIC_CANVAS_SCHEDULE = [
  [570,80],[585,60],[600,60],[630,50],[660,50],[690,40],
  [720,40],[780,40],[810,40],[840,30],[870,30],[900,30],
].map(([minute, blankPercent]) => ({ minute, blankPercent }))
export function normalizeDynamicCanvasSchedule(value) {
  if (!Array.isArray(value) || !value.length || value.length > 24
    || value.some(r => !Number.isInteger(r?.minute) || r.minute < 570 || r.minute >= 960
      || !Number.isFinite(r.blankPercent) || r.blankPercent < 1 || r.blankPercent > 95)
    || !value.some(r => r.minute === 570) || new Set(value.map(r => r.minute)).size !== value.length) {
    return DEFAULT_DYNAMIC_CANVAS_SCHEDULE.map(r => ({ ...r }))
  }
  return value.map(({minute, blankPercent}) => ({minute, blankPercent})).sort((a,b) => a.minute-b.minute)
}

// Candle rawEpochMs is a real instant. `time` is display-zone shifted and is
// used ONLY to ask LWC for its shared logical index (including indicator data).
export function dynamicCanvasView({ candles, timeframe, schedule, candlesBeforeOpen = 5, now = Date.now(), timeToIndex }) {
  if (!DYNAMIC_CANVAS_TIMEFRAMES.includes(timeframe) || !candles?.length) return null
  const last = candles.at(-1)
  const dateKey = getEtSessionDateKey(last.rawEpochMs)
  if (!dateKey) return null
  const openMs = toChartTimestamp(`${dateKey.replaceAll('-', '')} 09:30:00 US/Eastern`, 'UTC') * 1000
  const openIndex = candles.findIndex(b => b.rawEpochMs === openMs)
  if (openIndex < 0) return null
  const isCurrentSession = getEtSessionDateKey(now) === dateKey
  const effectiveNow = isCurrentSession ? now : last.rawEpochMs
  const minute = Math.max(570, Math.min(959, 570 + Math.floor((effectiveNow-openMs)/60000)))
  const row = normalizeDynamicCanvasSchedule(schedule).filter(r => r.minute <= minute).at(-1)
  const nativeIndex = i => {
    const value = timeToIndex?.(candles[i].time)
    return Number.isFinite(value) ? value : i
  }
  const before = Math.max(0, Math.min(100, Math.round(candlesBeforeOpen)))
  // Count actual prior price bars, even when auxiliary series add axis points.
  const from = openIndex >= before ? nativeIndex(openIndex-before) : nativeIndex(openIndex)-before
  const lastIndex = nativeIndex(candles.length-1)
  const span = Math.max(1, lastIndex-from)
  return {
    range: {from, to: from+span/(1-row.blankPercent/100)},
    dateKey, slotKey: `${dateKey}|${row.minute}`, blankPercent: row.blankPercent,
    isCurrentSession, regular: getMarketSession(new Date(now)) === 'regular',
  }
}

// No viewport writes on ordinary ticks. An identical percentage at a later
// scheduled time is still a new checkpoint and therefore reframes once.
export function createDynamicCanvasController({getInput, apply}) {
  let paused = false, session = null, slot = null
  function run(force = false) {
    const input = getInput()
    if (!input) return false
    const view = dynamicCanvasView(input)
    if (!view) return false
    if (session && session !== view.dateKey) { paused=false; slot=null }
    session=view.dateKey
    if (!force && (!view.isCurrentSession || !view.regular || paused || slot === view.slotKey)) return false
    if (apply(view.range) === false) return false
    slot=view.slotKey
    return true
  }
  return { tick: () => run(), pause: () => {paused=true}, reset: () => {paused=false;return run(true)} }
}
